(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const root = () => window.Shopify?.routes?.root || '/';
  function getRailItems(rail) {
    return [...rail.querySelectorAll('[data-aj-rail-item], .aj-product-cell, .aj-lookbook-card, .aj-testimonials figure')];
  }
  function getRailStops(rail) {
    const cells = getRailItems(rail);
    const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
    if (!cells.length) return [0, max];
    const stops = cells.map(cell => Math.min(Math.max(0, cell.offsetLeft), max));
    if (stops[stops.length - 1] < max) stops.push(max);
    return [...new Set(stops)].sort((a, b) => a - b);
  }
  function getRailStep(rail) {
    const cell = rail.querySelector('[data-aj-rail-item]')
      || rail.querySelector('.aj-product-cell:not(.aj-product-cell--promo)')
      || rail.querySelector('.aj-product-cell')
      || rail.querySelector('.aj-lookbook-card');
    if (!cell) return Math.max(rail.clientWidth * 0.5, 280);
    const styles = getComputedStyle(rail);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return Math.max(cell.getBoundingClientRect().width + gap, 1);
  }
  function nextRailOffset(rail, dir) {
    const stops = getRailStops(rail);
    const x = rail.scrollLeft;
    if (dir > 0) {
      return stops.find(stop => stop > x + 4) ?? stops[stops.length - 1];
    }
    return [...stops].reverse().find(stop => stop < x - 4) ?? stops[0];
  }
  function smoothScrollRail(rail, delta) {
    if (!rail) return;
    const dir = delta === 0 ? 0 : (delta > 0 ? 1 : -1);
    if (!dir) return;
    const from = rail.scrollLeft;
    const to = nextRailOffset(rail, dir);
    const distance = to - from;
    if (Math.abs(distance) < 1) return;

    if (rail._ajScrollFrame) cancelAnimationFrame(rail._ajScrollFrame);
    rail._ajDestroyScroll?.();

    // Slide feel: longer glide, no snap fighting the animation
    const duration = Math.min(780, Math.max(560, Math.abs(distance) * 1.2));
    const start = performance.now();
    rail.style.scrollSnapType = 'none';
    rail.style.scrollBehavior = 'auto';
    rail.classList.add('is-rail-animating');

    const easeOutQuint = t => 1 - Math.pow(1 - t, 5);
    const tick = now => {
      const t = Math.min(1, (now - start) / duration);
      rail.scrollLeft = from + distance * easeOutQuint(t);
      rail.dispatchEvent(new Event('scroll'));
      if (t < 1) {
        rail._ajScrollFrame = requestAnimationFrame(tick);
      } else {
        rail.scrollLeft = to;
        rail.classList.remove('is-rail-animating');
        rail._ajScrollFrame = 0;
        rail.dispatchEvent(new Event('scroll'));
      }
    };
    rail._ajScrollFrame = requestAnimationFrame(tick);
    rail._ajDestroyScroll = () => {
      if (rail._ajScrollFrame) cancelAnimationFrame(rail._ajScrollFrame);
      rail._ajScrollFrame = 0;
      rail.classList.remove('is-rail-animating');
    };
  }
  function initialize(scope = document) {
    scope.querySelectorAll('[data-aj-header]').forEach(header => {
      if (header.dataset.ready) return;
      header.dataset.ready = 'true';
      const menus = [...header.querySelectorAll('[data-hover-menu]')];
      const drawer = header.querySelector('[data-aj-drawer]');
      const toggles = [...header.querySelectorAll('[data-aj-menu-toggle]')];
      const update = () => header.style.setProperty('--aj-menu-bottom', `${header.getBoundingClientRect().bottom}px`);
      const setDrawer = open => {
        header.toggleAttribute('data-mobile-open', open);
        if (drawer) {
          drawer.hidden = !open;
          drawer.setAttribute('aria-hidden', String(!open));
        }
        toggles.forEach(t => t.setAttribute('aria-expanded', String(open)));
        document.documentElement.classList.toggle('aj-drawer-open', open);
        update();
      };
      const close = () => {
        menus.forEach(m => { m.open = false; });
        setDrawer(false);
      };
      menus.forEach(menu => {
        let leave;
        menu.addEventListener('pointerenter', () => {
          if (!matchMedia('(min-width:1024px) and (hover:hover)').matches) return;
          clearTimeout(leave);
          menus.forEach(m => { if (m !== menu) m.open = false; });
          update();
          menu.open = true;
        });
        menu.addEventListener('pointerleave', () => {
          if (matchMedia('(min-width:1024px) and (hover:hover)').matches) leave = setTimeout(() => { menu.open = false; }, 160);
        });
        menu.addEventListener('toggle', () => {
          if (menu.open) {
            update();
            menus.forEach(m => { if (m !== menu) m.open = false; });
          }
        });
        menu.addEventListener('focusout', e => {
          if (!menu.contains(e.relatedTarget)) menu.open = false;
        });
      });
      header.querySelectorAll('[data-aj-room-menu]').forEach(root => {
        const tabs = [...root.querySelectorAll('[data-aj-room-tab]')];
        const panels = [...root.querySelectorAll('[data-aj-room-panel]')];
        if (!tabs.length || !panels.length) return;
        const activate = handle => {
          tabs.forEach(tab => {
            const on = tab.getAttribute('data-aj-room-tab') === handle;
            if (on) tab.setAttribute('aria-current', 'true');
            else tab.removeAttribute('aria-current');
          });
          panels.forEach(panel => {
            panel.hidden = panel.getAttribute('data-aj-room-panel') !== handle;
          });
        };
        tabs.forEach(tab => {
          const go = () => activate(tab.getAttribute('data-aj-room-tab'));
          tab.addEventListener('pointerenter', go);
          tab.addEventListener('focus', go);
        });
      });
      header.querySelectorAll('[data-aj-menu-close]').forEach(btn => btn.addEventListener('click', close));
      toggles.forEach(btn => btn.addEventListener('click', () => setDrawer(!header.hasAttribute('data-mobile-open'))));
      header.addEventListener('keydown', e => {
        if (e.key === 'Escape') {
          const active = menus.find(m => m.open);
          close();
          active?.querySelector('summary')?.focus();
        }
      });
      matchMedia('(min-width:1024px)').addEventListener('change', e => { if (e.matches) close(); });
    });
    scope.querySelectorAll('.aj-categories--circles .aj-category-track').forEach(track => {
      if (track.dataset.ajScrollReady) return;
      track.dataset.ajScrollReady = 'true';
      track.addEventListener('wheel', e => {
        if (track.scrollWidth <= track.clientWidth + 1) return;
        const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        if (!delta) return;
        e.preventDefault();
        track.scrollLeft += delta;
      }, { passive: false });
    });
    scope.querySelectorAll('[data-aj-slider]').forEach(slider => {
      if (slider.dataset.ready) return;
      slider.dataset.ready = 'true';
      const track = slider.querySelector('.aj-hero-track');
      const slides = [...slider.querySelectorAll('[data-aj-slide]')];
      const dots = [...slider.querySelectorAll('[data-aj-dot]')];
      let index = 0;
      const go = n => {
        if (!track || !slides.length) return;
        index = (n + slides.length) % slides.length;
        track.scrollTo({ left: index * track.clientWidth, behavior: reduced.matches ? 'auto' : 'smooth' });
        dots.forEach((d, i) => d.setAttribute('aria-current', String(i === index)));
      };
      slider.querySelector('[data-aj-prev]')?.addEventListener('click', () => go(index - 1));
      slider.querySelector('[data-aj-next]')?.addEventListener('click', () => go(index + 1));
      dots.forEach((dot, i) => dot.addEventListener('click', () => go(i)));
      track?.addEventListener('scroll', () => {
        if (!track.clientWidth) return;
        index = Math.round(track.scrollLeft / track.clientWidth);
        dots.forEach((d, i) => d.setAttribute('aria-current', String(i === index)));
      }, { passive: true });
    });
    scope.querySelectorAll('[data-aj-ticker]').forEach(ticker => {
      if (ticker.dataset.ready) return;
      ticker.dataset.ready = 'true';
      const track = ticker.querySelector('[data-aj-ticker-track]');
      const group = ticker.querySelector('.aj-ticker-group');
      if (!track || !group) return;

      let offset = 0;
      let raf = 0;
      let last = 0;
      let paused = false;
      const autoplay = ticker.dataset.autoplay !== 'false';

      const loopWidth = () => Math.max(group.offsetWidth, 1);
      const seconds = () => Math.max(12, Math.min(80, Number(ticker.dataset.speed) || 30));
      const pxPerMs = () => loopWidth() / (seconds() * 1000);

      const tick = now => {
        if (!last) last = now;
        const dt = Math.min(48, now - last);
        last = now;
        if (autoplay && !paused) {
          offset += pxPerMs() * dt;
          const w = loopWidth();
          if (offset >= w) offset -= w;
          track.style.transform = `translate3d(${-offset}px,0,0)`;
        }
        raf = requestAnimationFrame(tick);
      };

      const start = () => {
        cancelAnimationFrame(raf);
        last = 0;
        raf = requestAnimationFrame(tick);
      };

      ticker.addEventListener('pointerenter', () => { paused = true; });
      ticker.addEventListener('pointerleave', () => { paused = false; last = 0; });
      ticker.addEventListener('focusin', () => { paused = true; });
      ticker.addEventListener('focusout', () => { paused = false; last = 0; });

      if (autoplay) start();
      const resize = new ResizeObserver(() => {
        const w = loopWidth();
        if (offset >= w) offset = offset % w;
      });
      resize.observe(group);
      ticker._ajDestroy = () => {
        cancelAnimationFrame(raf);
        resize.disconnect();
      };
    });
    scope.querySelectorAll('[data-aj-tabs]').forEach(group => {
      if (group.dataset.ready) return; group.dataset.ready = 'true';
      const tabs = [...group.querySelectorAll('[data-aj-tab]')];
      const activate = tab => tabs.forEach(t => { const active = t === tab; t.setAttribute('aria-selected', String(active)); t.tabIndex = active ? 0 : -1; const panel = document.getElementById(t.getAttribute('aria-controls')); if (panel) { panel.hidden = !active; if (active) panel.querySelector('[data-aj-rail]')?.dispatchEvent(new Event('scroll')); } });
      tabs.forEach((tab, i) => { tab.addEventListener('click', () => activate(tab)); tab.addEventListener('keydown', e => { let n; if (e.key === 'ArrowRight') n = (i + 1) % tabs.length; if (e.key === 'ArrowLeft') n = (i - 1 + tabs.length) % tabs.length; if (e.key === 'Home') n = 0; if (e.key === 'End') n = tabs.length - 1; if (n !== undefined) { e.preventDefault(); activate(tabs[n]); tabs[n].focus(); } }); });
    });
    scope.querySelectorAll('[data-aj-collection-tabs]').forEach(group => {
      if (group.dataset.ready) return;
      group.dataset.ready = 'true';
      const tabs = [...group.querySelectorAll('[data-aj-ctab]')];
      const image = group.querySelector('[data-aj-ctab-image]');
      const activate = tab => {
        tabs.forEach(t => {
          const active = t === tab;
          const item = t.closest('[data-aj-ctab-item]');
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-expanded', String(active));
          if (item) item.classList.toggle('is-open', active);
          if (panel) panel.hidden = !active;
        });
        if (image && tab.dataset.image) {
          image.src = tab.dataset.image;
          image.alt = tab.dataset.alt || tab.textContent.trim();
        }
      };
      tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => activate(tab));
        tab.addEventListener('keydown', e => {
          let n;
          if (e.key === 'ArrowDown') n = (i + 1) % tabs.length;
          if (e.key === 'ArrowUp') n = (i - 1 + tabs.length) % tabs.length;
          if (e.key === 'Home') n = 0;
          if (e.key === 'End') n = tabs.length - 1;
          if (n === undefined) return;
          e.preventDefault();
          activate(tabs[n]);
          tabs[n].focus();
        });
      });
      group._ajActivateCtab = activate;
    });
    scope.querySelectorAll('[data-aj-favorites]').forEach(section => {
      if (section.dataset.ready) return;
      section.dataset.ready = 'true';
      const track = section.querySelector('[data-aj-fav-track]');
      const slides = [...section.querySelectorAll('.aj-favorites__slide')];
      if (!track || !slides.length) return;
      let index = 0;
      const paint = () => {
        track.style.transform = `translateX(-${index * 100}%)`;
        section.querySelectorAll('[data-aj-fav-status]').forEach(el => {
          el.textContent = `${index + 1}/${slides.length}`;
        });
      };
      section.querySelectorAll('[data-aj-fav-prev]').forEach(btn => btn.addEventListener('click', () => {
        index = (index - 1 + slides.length) % slides.length;
        paint();
      }));
      section.querySelectorAll('[data-aj-fav-next]').forEach(btn => btn.addEventListener('click', () => {
        index = (index + 1) % slides.length;
        paint();
      }));
      paint();
    });
    scope.querySelectorAll('[data-aj-countdown]').forEach(clock => {
      if (clock.dataset.ready) return;
      clock.dataset.ready = 'true';
      const end = Date.parse(clock.dataset.ajCountdown);
      if (!Number.isFinite(end)) { clock.hidden = true; return; }
      const units = {
        days: clock.querySelector('[data-unit="days"] strong'),
        hours: clock.querySelector('[data-unit="hours"] strong'),
        mins: clock.querySelector('[data-unit="mins"] strong'),
        secs: clock.querySelector('[data-unit="secs"] strong')
      };
      const pad = n => String(n).padStart(2, '0');
      let timer;
      const tick = () => {
        const left = Math.max(0, Math.floor((end - Date.now()) / 1000));
        if (!left) {
          clock.textContent = clock.dataset.ended || 'Ended';
          clearInterval(timer);
          return;
        }
        const values = {
          days: Math.floor(left / 86400),
          hours: Math.floor(left / 3600) % 24,
          mins: Math.floor(left / 60) % 60,
          secs: left % 60
        };
        if (units.days && units.hours && units.mins && units.secs) {
          units.days.textContent = pad(values.days);
          units.hours.textContent = pad(values.hours);
          units.mins.textContent = pad(values.mins);
          units.secs.textContent = pad(values.secs);
        } else {
          clock.textContent = [values.days, values.hours, values.mins, values.secs].map(pad).join(' : ');
        }
      };
      timer = setInterval(tick, 1000);
      tick();
      clock._ajDestroy = () => clearInterval(timer);
    });
    scope.querySelectorAll('[data-aj-rail]').forEach(rail => {
      if (rail.dataset.progressReady) return;
      const host = rail.closest('[role=tabpanel]') || rail.closest('section') || rail.parentElement;
      const progress = host?.querySelector('[data-aj-rail-progress]');
      const thumb = progress?.querySelector('[data-aj-rail-thumb], span');
      const nav = host?.querySelector('[data-aj-rail-nav]');
      if (!progress && !nav) return;
      rail.dataset.progressReady = 'true';
      const metrics = () => {
        const max = Math.max(0, rail.scrollWidth - rail.clientWidth);
        const track = progress?.clientWidth || 0;
        const thumbW = max > 0 && track ? Math.max(track * (rail.clientWidth / rail.scrollWidth), 40) : track;
        const travel = Math.max(0, track - thumbW);
        const ratio = max > 0 ? rail.scrollLeft / max : 0;
        return { max, thumbW, travel, ratio };
      };
      const paint = () => {
        const { max, thumbW, travel, ratio } = metrics();
        const canScroll = max > 1;
        if (thumb && progress) {
          thumb.style.width = `${thumbW}px`;
          thumb.style.transform = `translateX(${ratio * travel}px)`;
          progress.setAttribute('aria-valuemin', '0');
          progress.setAttribute('aria-valuemax', String(Math.round(max)));
          progress.setAttribute('aria-valuenow', String(Math.round(rail.scrollLeft)));
          progress.hidden = false;
        }
        if (nav) {
          nav.hidden = false;
          nav.setAttribute('aria-hidden', 'false');
          nav.querySelectorAll('button').forEach(btn => {
            btn.disabled = !canScroll;
            btn.tabIndex = canScroll ? 0 : -1;
          });
        }
        // Keep controls visible; only mark state for styling
        host?.classList.toggle('aj-rail--no-scroll', !canScroll);
      };
      const scrollToRatio = (ratio, behavior = 'auto') => {
        const { max } = metrics();
        rail.scrollTo({ left: Math.max(0, Math.min(1, ratio)) * max, behavior });
      };
      const ratioFromPointer = clientX => {
        if (!progress) return 0;
        const rect = progress.getBoundingClientRect();
        const { thumbW, travel } = metrics();
        if (travel <= 0) return 0;
        const x = clientX - rect.left - thumbW / 2;
        return x / travel;
      };
      let dragging = false;
      const onPointerMove = e => {
        if (!dragging) return;
        scrollToRatio(ratioFromPointer(e.clientX));
      };
      const stopDrag = () => {
        if (!dragging) return;
        dragging = false;
        progress?.classList.remove('is-dragging');
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', stopDrag);
      };
      if (progress) {
        progress.addEventListener('pointerdown', e => {
          if (progress.hidden) return;
          if (e.button !== 0 && e.pointerType === 'mouse') return;
          e.preventDefault();
          dragging = true;
          progress.classList.add('is-dragging');
          scrollToRatio(ratioFromPointer(e.clientX));
          window.addEventListener('pointermove', onPointerMove);
          window.addEventListener('pointerup', stopDrag);
        });
        progress.addEventListener('keydown', e => {
          if (progress.hidden) return;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            smoothScrollRail(rail, 1);
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            smoothScrollRail(rail, -1);
          } else if (e.key === 'Home') {
            e.preventDefault();
            rail.scrollLeft = 0;
            rail.dispatchEvent(new Event('scroll'));
          } else if (e.key === 'End') {
            e.preventDefault();
            rail.scrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
            rail.dispatchEvent(new Event('scroll'));
          }
        });
      }
      rail.addEventListener('scroll', paint, { passive: true });
      const resize = new ResizeObserver(paint);
      resize.observe(rail);
      if (progress) resize.observe(progress);
      [...rail.children].forEach(child => resize.observe(child));
      requestAnimationFrame(() => requestAnimationFrame(paint));
      rail._ajDestroy = () => {
        resize.disconnect();
        stopDrag();
      };
    });
  }
  let selected;
  try { const stored = JSON.parse(sessionStorage.getItem('ajeg-compare') || '[]'); selected = Array.isArray(stored) ? stored.filter(h => typeof h === 'string' && /^[a-z0-9-]+$/.test(h)).slice(0,5) : []; } catch { selected = []; }
  const strings = () => JSON.parse(document.getElementById('AjegStrings')?.textContent || '{}');
  function syncCompare() {
    try { sessionStorage.setItem('ajeg-compare', JSON.stringify(selected)); } catch {}
    document.querySelectorAll('[data-aj-compare]').forEach(c => c.checked = selected.includes(c.dataset.ajCompare));
    document.querySelectorAll('[data-aj-compare-count]').forEach(c => c.textContent = selected.length);
    document.querySelectorAll('[data-aj-compare-open]').forEach(b => b.disabled = selected.length < 2);
    const bar = document.querySelector('[data-aj-compare-bar]'); if (bar) bar.hidden = !selected.length;
  }
  function textElement(tag, text) { const el = document.createElement(tag); el.textContent = text ?? '-'; return el; }
  async function compare() {
    const dialog = document.getElementById('AjegCompare'), content = dialog.querySelector('[data-aj-compare-content]');
    content.replaceChildren(textElement('p', '…')); if (!dialog.open) dialog.showModal();
    try {
      const products = await Promise.all(selected.map(async h => { const res = await fetch(`${root()}products/${encodeURIComponent(h)}.js`); if (!res.ok) throw new Error('product'); return res.json(); }));
      const s = strings(), table = document.createElement('table');
      const money = price => new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency:window.Shopify?.currency?.active || 'IDR'}).format(price / 100);
      const rows = [['title', p => p.title], ['price', p => money(p.price)], ['vendor', p => p.vendor], ['type', p => p.type], ['options', p => p.options.map(o => `${o.name}: ${o.values.join(', ')}`).join(' / ')], ['description', p => { const html = new DOMParser().parseFromString(p.description || '', 'text/html'); return html.body.textContent; }]];
      rows.forEach(([key, val]) => { const tr = document.createElement('tr'); const th = textElement('th', s[key]); th.scope = 'row'; tr.append(th); products.forEach(p => { const td = document.createElement('td'); if (key === 'title') { if (p.featured_image) {const image = document.createElement('img'); image.src = p.featured_image; image.alt = p.title; td.append(image);} const a = textElement('a', val(p)); a.href = `${root()}products/${encodeURIComponent(p.handle)}`; td.append(a); } else td.textContent = val(p) || '-'; tr.append(td); }); table.append(tr); }); content.replaceChildren(table);
    } catch { content.replaceChildren(textElement('p', strings().error)); }
  }
  function setAjSortOpen(sort, open, restoreFocus = false) {
    const toggle = sort.querySelector('[data-aj-sort-toggle]');
    const menu = sort.querySelector('[data-aj-sort-menu]');
    if (!toggle || !menu) return;
    sort.toggleAttribute('data-open', open);
    toggle.setAttribute('aria-expanded', String(open));
    menu.hidden = !open;
    if (open) {
      const current = menu.querySelector('[aria-selected="true"]') || menu.querySelector('[data-aj-sort-option]');
      current?.focus();
      current?.scrollIntoView({ block: 'nearest' });
    } else if (restoreFocus) toggle.focus();
  }
  function closeAjSorts(except) {
    document.querySelectorAll('[data-aj-sort][data-open]').forEach(sort => {
      if (sort !== except) setAjSortOpen(sort, false);
    });
  }
  function applyAjSortOption(option) {
    const sort = option.closest('[data-aj-sort]');
    const select = sort?.querySelector('select');
    if (!sort || !select) return;
    const value = option.dataset.value || '';
    sort.querySelector('[data-aj-sort-value]').textContent = option.textContent.trim();
    sort.querySelectorAll('[data-aj-sort-option]').forEach(item => {
      if (item === option) item.setAttribute('aria-selected', 'true');
      else item.removeAttribute('aria-selected');
    });
    setAjSortOpen(sort, false, true);
    if (select.value !== value) {
      select.value = value;
      select.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
  document.addEventListener('click', async e => {
    const sortRoot = e.target.closest('[data-aj-sort]');
    if (!sortRoot) closeAjSorts();
    const sortToggle = e.target.closest('[data-aj-sort-toggle]');
    if (sortToggle) {
      const sort = sortToggle.closest('[data-aj-sort]');
      const open = !sort.hasAttribute('data-open');
      closeAjSorts(sort);
      setAjSortOpen(sort, open);
    }
    const sortOption = e.target.closest('[data-aj-sort-option]');
    if (sortOption) applyAjSortOption(sortOption);
    const close = e.target.closest('[data-close-details]'); if (close) {const details = close.closest('details'); details.open = false; details.querySelector('summary')?.focus();}
    const scroller = e.target.closest('[data-aj-scroll]'); if (scroller) {e.preventDefault(); const parent = scroller.closest('[role=tabpanel]') || scroller.closest('section'); const rail = parent?.querySelector('[data-aj-rail]'); if (!rail || scroller.disabled) return; smoothScrollRail(rail, Number(scroller.dataset.ajScroll) || 0);}
    const copy = e.target.closest('[data-aj-copy]'); if (copy) {const status = copy.closest('section').querySelector('[data-aj-copy-status]'); try {await navigator.clipboard.writeText(copy.dataset.ajCopy); status.textContent = copy.dataset.copied;} catch {status.textContent = copy.dataset.ajCopy;}}
    if (e.target.closest('[data-aj-compare-clear]')) { selected = []; syncCompare(); }
    if (e.target.closest('[data-aj-compare-open]')) compare();
    if (e.target.closest('[data-aj-dialog-close]')) e.target.closest('dialog').close();
    const view = e.target.closest('[data-aj-view]'); if (view) {document.body.classList.toggle('aj-list-view', view.dataset.ajView === 'list'); document.querySelectorAll('[data-aj-view]').forEach(b => b.setAttribute('aria-pressed', String(b === view)));}
    const filter = e.target.closest('[data-aj-filter]');
    if (filter) {
      if (window.matchMedia('(max-width: 749px)').matches) {
        document.querySelector('#main-collection-filters .mobile-facets__disclosure > summary')?.click();
      } else {
        document.body.classList.toggle('aj-filters-collapsed');
        const open = !document.body.classList.contains('aj-filters-collapsed');
        document.querySelectorAll('[data-aj-filter]').forEach(b => {
          b.setAttribute('aria-pressed', String(open));
          b.setAttribute('aria-expanded', String(open));
        });
      }
    }
    const stickyAtc = e.target.closest('[data-aj-sticky-atc]');
    if (stickyAtc) {
      // Variant already chosen on PDP — trigger the real add-to-cart submit.
      const submit = document.querySelector('product-info .product-form__submit');
      if (submit && !submit.disabled) {
        submit.click();
        return;
      }
      // Fallback: no usable ATC (sold out / missing) — scroll to options.
      const picker =
        document.querySelector('variant-selects') ||
        document.querySelector('[id^="variant-selects-"]') ||
        document.querySelector('.product-form__input') ||
        document.querySelector('.product-form__buttons') ||
        document.querySelector('product-info');
      picker?.scrollIntoView({behavior: reduced.matches ? 'instant' : 'smooth', block: 'center'});
    }
  });
  document.addEventListener('keydown', e => {
    const sort = e.target.closest('[data-aj-sort]');
    if (!sort) return;
    const options = [...sort.querySelectorAll('[data-aj-sort-option]')];
    const index = options.indexOf(document.activeElement);
    if (e.key === 'Escape' && sort.hasAttribute('data-open')) {
      e.preventDefault();
      setAjSortOpen(sort, false, true);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!sort.hasAttribute('data-open')) {
        if (e.target.closest('[data-aj-sort-toggle]')) {
          e.preventDefault();
          setAjSortOpen(sort, true);
        }
        return;
      }
      e.preventDefault();
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      const next = options[(Math.max(index, 0) + delta + options.length) % options.length];
      next?.focus();
    }
    if ((e.key === 'Enter' || e.key === ' ') && document.activeElement?.matches('[data-aj-sort-option]')) {
      e.preventDefault();
      applyAjSortOption(document.activeElement);
    }
  });
  document.addEventListener('change', e => {
    if (e.target.matches('[data-aj-compare-mode]')) document.body.classList.toggle('aj-compare-mode', e.target.checked);
    if (e.target.matches('[data-aj-compare]')) {const h = e.target.dataset.ajCompare; if (e.target.checked && !selected.includes(h)) {if (selected.length >= 5) {e.target.checked = false; document.querySelector('[data-aj-compare-status]').textContent = strings().limit; return;} selected.push(h);} else selected = selected.filter(x => x !== h); syncCompare();}
  });
  function swatch(e) {
    const link = e.target.closest('[data-aj-swatch-image]');
    if (!link) return;
    const card = link.closest('.aj-card, .card');
    const image = card?.querySelector('.aj-card__image--primary, .card__media img');
    if (!image) return;
    image.src = link.dataset.ajSwatchImage;
    image.srcset = '';
  }
  function syncCollectionChrome() {
    const list = document.body.classList.contains('aj-list-view');
    document.querySelectorAll('[data-aj-view]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.ajView === 'list' ? list : !list)));
    const open = !document.body.classList.contains('aj-filters-collapsed');
    document.querySelectorAll('[data-aj-filter]').forEach(b => {
      b.setAttribute('aria-pressed', String(open));
      b.setAttribute('aria-expanded', String(open));
    });
  }
  const AJ_FACET_LABELS = {
    meja: 'Meja', kursi: 'Kursi', sofa: 'Sofa', lemari: 'Lemari', rak: 'Rak', kasur: 'Kasur', dekorasi: 'Dekorasi',
    living: 'Living', bedroom: 'Bedroom', office: 'Office', workspace: 'Workspace', storage: 'Storage'
  };
  function collectionTitleEls() {
    return {
      title: document.querySelector('[data-aj-collection-title]'),
      crumb: document.querySelector('.aj-collection-crumbs__current')
    };
  }
  function defaultCollectionTitle() {
    const title = document.querySelector('[data-aj-collection-title]');
    if (!title) return '';
    if (!title.dataset.ajDefault) title.dataset.ajDefault = title.textContent.trim();
    return title.dataset.ajDefault;
  }
  function syncAjFacetTitle(cats, rooms) {
    if (!/\/collections\/all\/?$/.test(location.pathname)) return;
    const els = collectionTitleEls();
    if (!els.title) return;
    const fallback = defaultCollectionTitle();
    const labels = [...cats, ...rooms].map(value => AJ_FACET_LABELS[value]).filter(Boolean);
    const next = labels.length ? labels.join(', ') : fallback;
    els.title.textContent = next;
    if (els.crumb) els.crumb.textContent = next;
  }
  function syncAjFacetUrl(cats, rooms) {
    if (!/\/collections\/all\/?$/.test(location.pathname)) return;
    const url = new URL(location.href);
    url.searchParams.delete('aj_cat');
    url.searchParams.delete('aj_room');
    cats.forEach(value => url.searchParams.append('aj_cat', value));
    rooms.forEach(value => url.searchParams.append('aj_room', value));
    const next = url.pathname + url.search + url.hash;
    if (next !== location.pathname + location.search + location.hash) history.replaceState({}, '', next);
  }
  function hydrateAjFacetsFromUrl() {
    const params = new URLSearchParams(location.search);
    params.getAll('aj_cat').forEach(value => {
      document.querySelectorAll(`[data-aj-facet="category"][value="${CSS.escape(value)}"]`).forEach(input => { input.checked = true; });
    });
    params.getAll('aj_room').forEach(value => {
      document.querySelectorAll(`[data-aj-facet="room"][value="${CSS.escape(value)}"]`).forEach(input => { input.checked = true; });
    });
  }
  function applyAjFacets() {
    const cats = [...new Set([...document.querySelectorAll('[data-aj-facet="category"]:checked')].map(i => i.value))];
    const rooms = [...new Set([...document.querySelectorAll('[data-aj-facet="room"]:checked')].map(i => i.value))];
    const types = new URLSearchParams(location.search).getAll('filter.p.tag').filter(Boolean);
    syncAjFacetTitle(cats, rooms);
    syncAjFacetUrl(cats, rooms);
    const run = () => {
      const items = document.querySelectorAll('#product-grid > .grid__item');
      if (!items.length) return;
      const count = document.getElementById('ProductCountDesktop');
      if (count && !count.dataset.ajCountOriginal) count.dataset.ajCountOriginal = count.textContent.trim();
      if (!cats.length && !rooms.length && !types.length) {
        items.forEach(item => { item.hidden = false; });
        if (count?.dataset.ajCountOriginal) count.textContent = count.dataset.ajCountOriginal;
        return;
      }
      let visible = 0;
      items.forEach(item => {
        const card = item.querySelector('[data-aj-cat], [data-aj-room], [data-aj-type], .aj-card');
        const itemCats = card?.dataset.ajCat || item.dataset.ajCat || '';
        const itemRooms = card?.dataset.ajRoom || item.dataset.ajRoom || '';
        const itemTypes = card?.dataset.ajType || item.dataset.ajType || '';
        const catOk = !cats.length || cats.some(c => itemCats.includes(`|${c}|`));
        const roomOk = !rooms.length || rooms.some(r => itemRooms.includes(`|${r}|`));
        const typeOk = !types.length || types.some(t => itemTypes.includes(`|${t}|`));
        const show = catOk && roomOk && typeOk;
        item.hidden = !show;
        if (show) visible += 1;
      });
      if (count) count.textContent = visible === 1 ? '1 product' : `${visible} products`;
    };
    if (cats.length || rooms.length || types.length) {
      run();
      ensureAllCollectionItems().then(run);
    } else run();
  }
  async function ensureAllCollectionItems() {
    const grid = document.getElementById('product-grid');
    if (!grid || grid.dataset.ajLoaded === 'all') return;
    const countEl = document.getElementById('ProductCountDesktop');
    // Read the ORIGINAL product count. run() has already overwritten the visible
    // text with the filtered count, so reading textContent here understates the
    // total, makes perPage >= total true, and skips the remaining pages.
    const rawTotal = countEl?.dataset.ajCountOriginal || countEl?.textContent || '';
    const total = Number(rawTotal.replace(/\D/g, '')) || grid.children.length;
    const perPage = grid.querySelectorAll(':scope > .grid__item').length;
    if (!perPage || perPage >= total) {
      grid.dataset.ajLoaded = 'all';
      return;
    }
    const sectionId = grid.dataset.id;
    const pages = Math.ceil(total / perPage);
    for (let p = 2; p <= pages; p += 1) {
      try {
        const res = await fetch(`${window.location.pathname}?page=${p}&section_id=${encodeURIComponent(sectionId)}`);
        if (!res.ok) continue;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        doc.getElementById('product-grid')?.querySelectorAll(':scope > .grid__item').forEach(li => {
          grid.appendChild(document.importNode(li, true));
        });
      } catch {}
    }
    grid.dataset.ajLoaded = 'all';
    document.querySelector('#ProductGridContainer .pagination-wrapper, #ProductGridContainer nav')?.setAttribute('hidden', '');
  }
  document.addEventListener('input', e => {
    if (!e.target.closest('[data-aj-facet-groups]')) return;
    e.stopImmediatePropagation();
    const facet = e.target.closest('[data-aj-facet]');
    if (facet) {
      document.querySelectorAll(`[data-aj-facet="${facet.dataset.ajFacet}"][value="${CSS.escape(facet.value)}"]`).forEach(input => {
        if (input !== facet) input.checked = facet.checked;
      });
    }
    applyAjFacets();
  }, true);
  document.addEventListener('pointerover', swatch); document.addEventListener('focusin', swatch);
  document.addEventListener('shopify:section:load', e => {initialize(e.target); syncCompare(); syncCollectionChrome(); hydrateAjFacetsFromUrl(); applyAjFacets();});
  document.addEventListener('shopify:section:unload', e => e.target.querySelectorAll('[data-ready]').forEach(el => el._ajDestroy?.()));
  document.addEventListener('shopify:block:select', e => {
    const slide = e.target.closest('[data-aj-slide]');
    if (slide) {
      slide.closest('[data-aj-slider]')._ajDestroy?.();
      const track = slide.parentElement;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft });
    }
    const menu = e.target.closest('[data-hover-menu]');
    if (menu) menu.open = true;
    const ctabItem = e.target.closest('[data-aj-ctab-item]');
    const ctab = ctabItem?.querySelector('[data-aj-ctab]');
    const tabsGroup = ctabItem?.closest('[data-aj-collection-tabs]');
    if (ctab && tabsGroup?._ajActivateCtab) tabsGroup._ajActivateCtab(ctab);
  });
  function boot() {initialize(); syncCompare(); syncCollectionChrome(); hydrateAjFacetsFromUrl(); applyAjFacets(); const bar=document.querySelector('.aj-sticky-buy'); const form=document.querySelector('product-info .product-form'); if(bar&&form) {new IntersectionObserver(entries=>{bar.hidden=entries[0].isIntersecting;},{threshold:0}).observe(form);} }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
